import React, { useMemo } from 'react';
import { Subcontractor, StatementItem } from '../../../types';
import { KlFullModal } from '../modal/KlFullModal';

export interface MobileGenerateEPModalProps {
    open: boolean;
    onClose: () => void;
    currentDraftEp: any;
    subcontractor?: Subcontractor | null;
    epNumber: number;
    onOpenItemDetail: (item: StatementItem) => void;
    onSaveDraft: () => void;
    onAuthorize: () => void;
    canAuthorize: boolean;
}

export const MobileGenerateEPModal: React.FC<MobileGenerateEPModalProps> = ({
    open,
    onClose,
    currentDraftEp,
    subcontractor,
    epNumber,
    onOpenItemDetail,
    onSaveDraft,
    onAuthorize,
    canAuthorize
}) => {
    const items: StatementItem[] = currentDraftEp?.items || [];

    const totalValorAcumulado = useMemo(() => {
        return items.reduce((acc, i) => acc + (i.totalValue || 0), 0);
    }, [items]);

    const totalPagarAcumulado = useMemo(() => {
        return items.reduce((acc, i) => acc + (i.paidAmount || 0), 0);
    }, [items]);

    return (
        <KlFullModal
            open={open}
            onClose={onClose}
            title="Estado de pago"
            footer={
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-[0_0_28%] h-11 rounded-[12px] font-bold text-[14px] border border-[#CBD5E1] dark:border-slate-700 bg-white dark:bg-slate-800 text-[#334155] dark:text-slate-200 active:scale-[0.98] transition-transform"
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={onSaveDraft}
                        className="flex-1 h-11 rounded-[12px] font-bold text-[14px] border border-[#BFDBFE] dark:border-blue-900/60 bg-[#EFF6FF] dark:bg-blue-950/60 text-[#1D4ED8] dark:text-blue-300 active:scale-[0.98] transition-transform"
                    >
                        Guardar
                    </button>
                    <button
                        type="button"
                        onClick={onAuthorize}
                        disabled={!canAuthorize}
                        className={`flex-[1.1] h-11 rounded-[12px] font-bold text-[14px] text-white transition-all active:scale-[0.98] ${
                            canAuthorize
                                ? 'bg-[#16A34A] hover:bg-green-700 shadow-md'
                                : 'bg-slate-300 dark:bg-slate-700 text-slate-500 cursor-not-allowed'
                        }`}
                    >
                        Autorizar
                    </button>
                </div>
            }
        >
            <div className="flex flex-col gap-2.5 pb-6">
                {/* TARJETA 1: DATOS GENERALES DEL SUBCONTRATO (10-estado-de-pago.html) */}
                <div className="bg-white dark:bg-slate-900 rounded-[14px] p-3 shadow-xs border border-slate-100 dark:border-slate-800 flex flex-col gap-1.5">
                    <div className="flex items-center gap-2">
                        <span className="text-[12px] font-bold px-2 py-0.5 rounded-[10px] bg-[#DBEAFE] text-[#1E40AF] dark:bg-blue-950 dark:text-blue-300 shrink-0">
                            EP #{epNumber}
                        </span>
                        <b className="text-[14px] text-[#0F172A] dark:text-slate-100 truncate">
                            {subcontractor?.razonSocial || 'Subcontratista'}
                        </b>
                    </div>
                    <div className="text-[12px] text-[#64748B] dark:text-slate-400 font-mono">
                        R.U.T. {subcontractor?.rut || 'Sin RUT'}
                    </div>
                </div>

                {/* TARJETAS POR PARTIDA (10-estado-de-pago.html) */}
                {items.length === 0 ? (
                    <div className="bg-white dark:bg-slate-900 rounded-[14px] p-6 text-center text-slate-400 italic text-xs">
                        No hay partidas asociadas en este contrato.
                    </div>
                ) : (
                    items.map((item) => (
                        <div
                            key={item.id}
                            onClick={() => onOpenItemDetail(item)}
                            className="bg-white dark:bg-slate-900 rounded-[14px] p-3 shadow-xs border border-slate-100 dark:border-slate-800 flex flex-col gap-2 cursor-pointer active:bg-blue-50/40 transition-colors"
                        >
                            <div className="flex gap-2 items-start">
                                <span className="font-mono text-[11px] font-semibold px-2 py-0.5 rounded-[10px] bg-[#F1F5F9] dark:bg-slate-800 text-[#64748B] dark:text-slate-300 shrink-0">
                                    {item.code || '-'}
                                </span>
                                <b className="flex-1 text-[14px] leading-snug text-[#0F172A] dark:text-slate-100">
                                    {item.description}
                                </b>
                            </div>

                            <div className="grid grid-cols-2 gap-2.5 pt-1 border-t border-slate-100 dark:border-slate-800">
                                <div>
                                    <div className="text-[10px] font-semibold uppercase tracking-wider text-[#64748B] dark:text-slate-400">
                                        Valor acumulado
                                    </div>
                                    <div className="text-[14px] font-bold text-[#0F172A] dark:text-slate-100 font-mono mt-0.5">
                                        ${(item.totalValue || 0).toLocaleString('es-CL')}
                                    </div>
                                </div>

                                <div>
                                    <div className="text-[10px] font-semibold uppercase tracking-wider text-[#1D4ED8] dark:text-blue-400">
                                        Monto a pagar acumulado
                                    </div>
                                    <div className="h-9 border border-[#E2E8F0] dark:border-slate-700 rounded-[10px] bg-slate-50 dark:bg-slate-800/60 px-3 flex items-center justify-between mt-1">
                                        <span className="font-mono text-[13px] font-extrabold text-[#1D4ED8] dark:text-blue-300">
                                            ${(item.paidAmount || 0).toLocaleString('es-CL')}
                                        </span>
                                        <svg className="w-4 h-4 text-[#94A3B8]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="m9 18 6-6-6-6" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                )}

                {/* TARJETA RESUMEN TOTALES (10-estado-de-pago.html) */}
                <div className="bg-white dark:bg-slate-900 rounded-[14px] p-3 shadow-xs border border-slate-100 dark:border-slate-800 flex flex-col gap-1.5">
                    <div className="flex justify-between items-center text-[13px]">
                        <span className="text-[#64748B] dark:text-slate-400 font-medium">
                            Total valor acumulado
                        </span>
                        <b className="text-[14px] font-bold text-[#0F172A] dark:text-slate-100 font-mono">
                            ${totalValorAcumulado.toLocaleString('es-CL')}
                        </b>
                    </div>
                    <div className="flex justify-between items-center text-[13px] pt-1 border-t border-slate-100 dark:border-slate-800">
                        <span className="text-[#64748B] dark:text-slate-400 font-medium">
                            Total a pagar acumulado
                        </span>
                        <b className="text-[15px] font-extrabold text-[#1D4ED8] dark:text-blue-400 font-mono">
                            ${totalPagarAcumulado.toLocaleString('es-CL')}
                        </b>
                    </div>
                </div>
            </div>
        </KlFullModal>
    );
};
